(() => {
  const initSection = (sectionEl) => {
    if (!sectionEl || sectionEl.dataset.pressReleasesInitialized === "true") return;

    const configEl = sectionEl.querySelector("[data-press-releases-config]");
    const listEl = sectionEl.querySelector("[data-press-releases-list]");
    const statusEl = sectionEl.querySelector("[data-press-releases-status]");

    if (!configEl || !listEl || !statusEl) return;

    sectionEl.dataset.pressReleasesInitialized = "true";

    let config;

    try {
      config = JSON.parse(configEl.textContent || "{}");
    } catch (error) {
      console.error("[Press releases] Invalid section configuration:", error);
      return;
    }

    const token = config.storefrontToken;
    const apiVersion = config.apiVersion || "2026-01";
    const metaobjectType = config.metaobjectType || "press_releases";
    const fallbackBasePath = config.fallbackBasePath || "/pages/press-releases/";
    const endpoint = `${window.location.origin}/api/${apiVersion}/graphql.json`;
    const fetchPageSize = 50;

    const query = `
      query PressReleasesPage($type: String!, $first: Int!, $after: String) {
        metaobjects(type: $type, first: $first, after: $after) {
          edges {
            node {
              id
              handle
              onlineStoreUrl
              fields {
                key
                value
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    `;

    const escapeHtml = (value) =>
      String(value ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");

    const fieldValue = (node, key) =>
      node.fields?.find((field) => field.key === key)?.value || "";

    const parseDate = (rawDate) => {
      if (!rawDate) return null;

      const dateOnly = String(rawDate).match(/^(\d{4})-(\d{2})-(\d{2})$/);
      const date = dateOnly
        ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
        : new Date(rawDate);

      return Number.isNaN(date.getTime()) ? null : date;
    };

    const formatDate = (rawDate) => {
      const date = parseDate(rawDate);

      if (!date) return "";

      return new Intl.DateTimeFormat("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric"
      }).format(date);
    };

    const fallbackUrl = (handle) =>
      `${fallbackBasePath.replace(/\/$/, "")}/${encodeURIComponent(handle)}`;

    const storefrontUrl = (onlineStoreUrl, handle) => {
      if (!onlineStoreUrl) return fallbackUrl(handle);

      try {
        const url = new URL(onlineStoreUrl, window.location.origin);
        return `${url.pathname}${url.search}${url.hash}`;
      } catch (error) {
        console.warn("[Press releases] Invalid online store URL:", onlineStoreUrl, error);
        return fallbackUrl(handle);
      }
    };

    const normalize = (node) => {
      const dateRaw = fieldValue(node, "release_date");

      return {
        id: node.id,
        handle: node.handle,
        title: fieldValue(node, "title"),
        subtitle: fieldValue(node, "subtitle"),
        dateRaw,
        date: parseDate(dateRaw),
        url: storefrontUrl(node.onlineStoreUrl, node.handle)
      };
    };

    const fetchPage = async (after) => {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": token
        },
        body: JSON.stringify({
          query,
          variables: {
            type: metaobjectType,
            first: fetchPageSize,
            after
          }
        })
      });

      const payload = await response.json();

      if (!response.ok) {
        throw new Error(`Storefront request failed with HTTP ${response.status}`);
      }

      if (payload.errors?.length) {
        console.error("[Press releases] GraphQL errors:", payload.errors);
        throw new Error("Storefront query returned GraphQL errors");
      }

      return payload.data?.metaobjects;
    };

    const fetchAll = async () => {
      const items = [];
      let after = null;
      let hasNextPage = true;

      while (hasNextPage) {
        const connection = await fetchPage(after);

        if (!connection) break;

        connection.edges?.forEach(({ node }) => items.push(normalize(node)));

        after = connection.pageInfo?.endCursor || null;
        hasNextPage = Boolean(connection.pageInfo?.hasNextPage);
      }

      return items.sort((a, b) => {
        const dateDifference = (b.date?.getTime() || 0) - (a.date?.getTime() || 0);
        return dateDifference || a.title.localeCompare(b.title);
      });
    };

    const renderItem = (item) => {
      const subtitle = item.subtitle
        ? `<p class="press-releases-list__subtitle mt0p5 mb0">${escapeHtml(item.subtitle)}</p>`
        : "";
      const formattedDate = formatDate(item.dateRaw);
      const date = formattedDate
        ? `<time class="press-releases-list__date subtitle" datetime="${escapeHtml(item.dateRaw)}">${escapeHtml(formattedDate)}</time>`
        : "";

      return `
        <li class="press-releases-list__item">
          <a class="press-releases-list__link" href="${escapeHtml(item.url)}">
            <div class="press-releases-list__copy">
              <h2 class="press-releases-list__title title-primary size--xs mt0 mb0">${escapeHtml(item.title)}</h2>
              ${subtitle}
            </div>
            ${date}
          </a>
        </li>
      `;
    };

    const render = (items) => {
      if (!items.length) {
        listEl.hidden = true;
        statusEl.hidden = false;
        statusEl.textContent = config.emptyText || "No press releases are available yet.";
        return;
      }

      listEl.innerHTML = items.map(renderItem).join("");
      listEl.hidden = false;
      statusEl.hidden = true;
    };

    const fail = (error) => {
      console.error("[Press releases] Failed to load:", error);
      listEl.hidden = true;
      statusEl.hidden = false;
      statusEl.textContent = config.errorText || "Press releases could not be loaded. Please try again.";
    };

    if (!token) {
      fail(new Error("Missing Storefront API token"));
      return;
    }

    statusEl.hidden = false;
    statusEl.textContent = config.loadingText || "Loading press releases...";

    fetchAll().then(render).catch(fail);
  };

  const initAll = (root = document) => {
    if (root.matches?.("[data-press-releases-section]")) initSection(root);
    root.querySelectorAll?.("[data-press-releases-section]").forEach(initSection);
  };

  initAll();

  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
  });
})();
